import { Price } from '@wix/package-picker-components';
import { Experiments, PlanType, ProductChangeType } from 'enums';
import { observer } from 'mobx-react';
import { AdvancedPlansProductModel } from 'models/AdvancedPlansProductModel';
import { RootStore, withInjectedRootStore } from 'providers';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { taxIncludedTranslationKey } from '../../../../../../constants';

export interface ProductPriceProps {
  product: AdvancedPlansProductModel;
  size: 'small' | 'tiny';
  rootStore: RootStore;
  inline?: boolean;
}

const ProductPriceComp: React.FC<ProductPriceProps> = ({
  product,
  size,
  rootStore,
  inline,
}) => {
  const { t } = useTranslation();
  const {
    appContext: { country, currentPlanDetails, isLimitedTimeDiscount },
    isInSale,
    pricesIncludeTax,
    currentCycle,
    isChangePlan,
    planType,
    experiments,
  } = rootStore.appStore;
  const cycle = currentPlanDetails?.paymentCycle || currentCycle;

  const unifiedChangePlanPhase1Enabled =
    experiments[Experiments.UnifiedChangePlanPhase1] === 'true';
  const shouldShowPriceByCycle =
    isChangePlan && !unifiedChangePlanPhase1Enabled;

  const fullPrice = shouldShowPriceByCycle
    ? product.getFullPriceByCycle(cycle)
    : product.getYearlyFullPrice();

  const getActualPrice = () => {
    if (shouldShowPriceByCycle) {
      return product.getActualPriceByCycle(cycle);
    }

    if (
      isChangePlan &&
      product.getProductChangeType() !== ProductChangeType.Upgrade
    ) {
      return fullPrice;
    }

    return (
      product.getFirstCycleYearlyMonthly() || product.getYearlyActualPrice()
    );
  };

  const isDayfulFreeProduct = product.getName() === 'Free';

  const _fullPrice = isDayfulFreeProduct ? 0 : fullPrice;
  const _actualPrice = isDayfulFreeProduct ? 0 : getActualPrice();

  const shouldShowSale =
    isChangePlan &&
    unifiedChangePlanPhase1Enabled &&
    product.getProductChangeType() !== ProductChangeType.Upgrade
      ? false
      : isInSale;

  const shouldShowProductFullPrice =
    shouldShowSale || (isLimitedTimeDiscount && planType === PlanType.Business);

  return (
    <Price
      size={size}
      price={_actualPrice}
      fullPrice={_fullPrice}
      cycleLabel={`/${t('product_price.month')}`}
      taxIncludedLabel={t(taxIncludedTranslationKey[country])}
      shouldShowTaxIncludedLabel={pricesIncludeTax}
      shouldShowProductFullPrice={shouldShowProductFullPrice}
      inline={inline}
    />
  );
};

export const ProductPrice = observer(withInjectedRootStore(ProductPriceComp));
